import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  REFRESH_TOKEN_KEY,
  REMEMBER_EMAIL_KEY,
  REMEMBER_FLAG_KEY,
  USER_TOKEN_KEY,
} from "@shared/constants";

interface StorageAdapter {
  set: (key: string, value: string) => Promise<void> | void;
  get: (key: string) => Promise<string | null> | string | null;
  remove: (key: string) => Promise<void> | void;
}

@Injectable({
  providedIn: "root",
})
export class SecureStorageService {
  private readonly adapter: StorageAdapter;
  private readonly isNativePlatform = Capacitor.isNativePlatform();

  constructor() {
    this.adapter = this.isNativePlatform
      ? {
          set: (key, value) => Preferences.set({ key, value }),
          get: (key) => Preferences.get({ key }).then((r) => r.value),
          remove: (key) => Preferences.remove({ key }),
        }
      : {
          set: (key, value) => localStorage.setItem(key, value),
          get: (key) => localStorage.getItem(key),
          remove: (key) => localStorage.removeItem(key),
        };
  }

  async setJwtToken(token: string): Promise<void> {
    await this.adapter.set(USER_TOKEN_KEY, token);
  }

  async getJwtToken(): Promise<string | null> {
    return this.adapter.get(USER_TOKEN_KEY);
  }

  async removeJwtToken(): Promise<void> {
    await this.adapter.remove(USER_TOKEN_KEY);
  }

  async setRefreshToken(token: string): Promise<void> {
    if (this.isNativePlatform) {
      await this.adapter.set(REFRESH_TOKEN_KEY, token);
    } else {
      const maxAge = 7 * 24 * 60 * 60;
      document.cookie = `${REFRESH_TOKEN_KEY}=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }
  }

  async getRefreshToken(): Promise<string | null> {
    if (this.isNativePlatform) {
      return this.adapter.get(REFRESH_TOKEN_KEY);
    }

    const name = REFRESH_TOKEN_KEY + "=";
    const decoded = decodeURIComponent(document.cookie);
    for (const part of decoded.split(";")) {
      const cookie = part.trim();
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length);
      }
    }

    return null;
  }

  async removeRefreshToken(): Promise<void> {
    if (this.isNativePlatform) {
      await this.adapter.remove(REFRESH_TOKEN_KEY);
    } else {
      document.cookie = `${REFRESH_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
  }

  private async setRememberNative(
    email: string,
    remember: boolean
  ): Promise<void> {
    if (remember) {
      await Promise.all([
        Preferences.set({ key: REMEMBER_EMAIL_KEY, value: email }),
        Preferences.set({ key: REMEMBER_FLAG_KEY, value: "yes" }),
      ]);
    } else {
      await Promise.all([
        Preferences.remove({ key: REMEMBER_EMAIL_KEY }),
        Preferences.remove({ key: REMEMBER_FLAG_KEY }),
      ]);
    }
  }

  private setRememberWeb(email: string, remember: boolean): void {
    const maxAge = 365 * 24 * 60 * 60;
    const expire = "Thu, 01 Jan 1970 00:00:00 GMT";

    const flagValue = remember ? "yes" : "";
    const flagMaxAge = remember ? `max-age=${maxAge}` : `expires=${expire}`;
    const emailValue = remember ? encodeURIComponent(email) : "";
    const emailMaxAge = remember ? `max-age=${maxAge}` : `expires=${expire}`;

    document.cookie = `${REMEMBER_FLAG_KEY}=${flagValue}; path=/; ${flagMaxAge}; SameSite=Lax`;
    document.cookie = `${REMEMBER_EMAIL_KEY}=${emailValue}; path=/; ${emailMaxAge}; SameSite=Lax`;
  }

  async setRememberMe(email: string, remember: boolean): Promise<void> {
    if (this.isNativePlatform) {
      await this.setRememberNative(email, remember);
    } else {
      this.setRememberWeb(email, remember);
    }
  }

  async getRememberMe(): Promise<{ email: string | null; remember: boolean }> {
    if (this.isNativePlatform) {
      const [emailRes, flagRes] = await Promise.all([
        Preferences.get({ key: REMEMBER_EMAIL_KEY }),
        Preferences.get({ key: REMEMBER_FLAG_KEY }),
      ]);

      return {
        email: emailRes.value,
        remember: flagRes.value === "yes",
      };
    }

    let email: string | null = null;
    let remember = false;

    const decoded = decodeURIComponent(document.cookie);
    for (const part of decoded.split(";")) {
      const c = part.trim();
      if (c.startsWith(REMEMBER_EMAIL_KEY + "=")) {
        email = decodeURIComponent(c.substring(REMEMBER_EMAIL_KEY.length + 1));
      }
      if (c.startsWith(REMEMBER_FLAG_KEY + "=")) {
        remember = c.substring(REMEMBER_FLAG_KEY.length + 1) === "yes";
      }
    }

    return { email, remember };
  }

  async clearRememberMe(): Promise<void> {
    if (this.isNativePlatform) {
      await Promise.all([
        Preferences.remove({ key: REMEMBER_EMAIL_KEY }),
        Preferences.remove({ key: REMEMBER_FLAG_KEY }),
      ]);
    } else {
      document.cookie = `${REMEMBER_FLAG_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      document.cookie = `${REMEMBER_EMAIL_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
  }

  async clearAll(): Promise<void> {
    await Promise.all([this.removeJwtToken(), this.removeRefreshToken()]);
  }
}
